import { motion } from 'framer-motion';
import { Cpu, HardDrive, Zap } from 'lucide-react';

const SelfHostingPage = () => {
  const hardwareOptions = [
    {
      tier: 'Budget',
      price: '$200-500',
      icon: HardDrive,
      specs: [
        'Raspberry Pi 4 (8GB) cluster',
        'Or old laptop/desktop',
        '100GB storage minimum',
        'Gigabit network'
      ],
      performance: 'Good for home networks up to 50 devices'
    },
    {
      tier: 'Recommended',
      price: '$500-1,500',
      icon: Cpu,
      specs: [
        'Used enterprise server (Dell R730)',
        'Intel Xeon (8+ cores)',
        '32GB RAM',
        '500GB SSD'
      ],
      performance: 'Excellent for 100+ devices, faster AI responses'
    },
    {
      tier: 'Performance',
      price: '$1,500-3,000',
      icon: Zap,
      specs: [
        'Modern GPU workstation',
        'NVIDIA RTX 3060+ (12GB VRAM)',
        '64GB RAM',
        '1TB NVMe SSD'
      ],
      performance: 'Best AI performance, sub-second responses, 200+ devices'
    }
  ];

  const quickStart = [
    {
      step: 1,
      title: 'Install Ubuntu Server',
      commands: [
        '# Download Ubuntu Server 24.04 LTS',
        '# Flash to USB with Rufus/balenaEtcher',
        '# Boot and follow installation wizard'
      ]
    },
    {
      step: 2,
      title: 'Install Dependencies',
      commands: [
        'sudo apt update && sudo apt upgrade -y',
        'sudo apt install -y python3.11 python3-pip postgresql docker.io nginx',
        'sudo systemctl enable --now postgresql docker nginx'
      ]
    },
    {
      step: 3,
      title: 'Clone MacMind (Coming Soon)',
      commands: [
        '# Repository will be published on GitHub',
        'git clone https://github.com/yourusername/macmind.git',
        'cd macmind',
        './install.sh  # One-command setup script'
      ]
    },
    {
      step: 4,
      title: 'Configure & Launch',
      commands: [
        'cp .env.example .env',
        'nano .env  # Set your preferences',
        'sudo systemctl start macmind-*',
        'echo "MacMind running at http://localhost:5173"'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-green-400 mb-4">
            Build Your Own MacMind
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Self-host a multi-agent AI system on your own hardware. Complete privacy, full control.
          </p>
        </motion.div>

        {/* Hardware Options */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6">Hardware Recommendations</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {hardwareOptions.map((option) => (
              <div
                key={option.tier}
                className="bg-black/50 border border-green-500/20 rounded-lg p-6 hover:border-green-500/40 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-green-400">{option.tier}</h3>
                  <option.icon className="w-10 h-10 text-green-400" />
                </div>
                <p className="text-xl text-gray-300 mb-4 font-semibold">{option.price}</p>
                <ul className="space-y-2 mb-4">
                  {option.specs.map((spec) => (
                    <li key={spec} className="text-gray-300 text-sm">
                      <span className="text-green-400 mr-2">▸</span>
                      {spec}
                    </li>
                  ))}
                </ul>
                <p className="text-gray-400 text-sm italic">{option.performance}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Quick Start */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6">Quick Start Guide</h2>
          <div className="space-y-6">
            {quickStart.map((section) => (
              <div
                key={section.step}
                className="bg-black/50 border border-green-500/20 rounded-lg p-6"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-black font-bold">
                    {section.step}
                  </div>
                  <h3 className="text-xl font-bold text-green-400">{section.title}</h3>
                </div>
                <div className="bg-black rounded-lg p-4 font-mono text-sm">
                  {section.commands.map((cmd, idx) => (
                    <div key={idx} className="text-green-400">
                      {cmd.startsWith('#') ? (
                        <span className="text-gray-500">{cmd}</span>
                      ) : (
                        <span>{cmd}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Coming Soon Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-gray-900/30 backdrop-blur border border-green-500/30 rounded-xl p-8 text-center mt-12"
        >
          <div className="text-green-400 text-4xl mb-4 font-mono">{'>'}_</div>
          <h3 className="text-2xl font-bold text-green-400 mb-4">Full Documentation Coming Soon</h3>
          <p className="text-gray-300">
            Complete installation guide, API documentation, and agent development tutorials will be published shortly.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SelfHostingPage;
